import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, message, Modal, Select, Space, Tag } from 'antd';
import * as Component from 'components';
import { CONTENT_TYPE } from 'constants/constant';
import { contentService } from 'content/service';
import { ContentListInterface } from 'content/library/types/contentType';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DistributionStatusModal from 'components/distribution/DistributionStatusModal';
import { accountStore } from 'store/accountStore';
import { TableColumnInterface } from 'type/tableType';
import { SERVICE_DISTRIBUTION_OPTIONS } from '../../movie/constants';
import ContentBulkDistributionButton from '../components/main/ContentBulkDistributionButton';
import { libraryService } from '../service/libraryService';
import './Library.css';

const INGEST_SOURCE_OPTIONS = [
  { label: 'In-House', value: 'in-house' },
  { label: 'Mediahold', value: 'mediahold' },
  { label: 'KCP', value: 'kcp' },
];

const Library: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '100',
  });
  const user = accountStore.getAccount();
  const isPlatformOne = user?.platform.id === 1;
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [serviceDistributionFilter, setServiceDistributionFilter] = useState<string[]>(
    searchParams.get('service_distribution')?.split(',').filter(Boolean) || []
  );
  const [ingestSourceFilter, setIngestSourceFilter] = useState<string[]>(
    searchParams.get('ingest_source')?.split(',').filter(Boolean) || []
  );
  const [contentTypeFilter, setContentTypeFilter] = useState<string[]>(
    searchParams.get('content_type')?.split(',').filter(Boolean) || []
  );
  const [checkTarget, setCheckTarget] = useState<{ id: number; title: string } | null>(null);

  const handleServiceDistributionChange = (values: string[]) => {
    setServiceDistributionFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('service_distribution', values.join(','));
    } else {
      newParams.delete('service_distribution');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleIngestSourceFilterChange = (values: string[]) => {
    setIngestSourceFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('ingest_source', values.join(','));
    } else {
      newParams.delete('ingest_source');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleContentTypeChange = (values: string[]) => {
    setContentTypeFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('content_type', values.join(','));
    } else {
      newParams.delete('content_type');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Thumbnail',
      dataIndex: 'poster',
      key: 'poster',
      width: '10%',
      render: (_text: string, record: ContentListInterface) => (
        <Component.ThumbnailComponent
          src={record.poster?.url}
          width={100}
          height={100}
          alt="thumbnail"
          emptyText="No Thumbnail"
          errorText="Load failed"
          preview
        />
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: true,
      render: (_text: string, record: ContentListInterface) => {
        const enTitle = record.i18n_title?.en || record.title_en || '';
        const koTitle = record.i18n_title?.ko || '';
        const zhTitle = record.i18n_title?.['zh-hans'] || '';
        return (
          <div
            onClick={() =>
              window.open(
                `/episode/edit/${record.id || 0}?content_type=${record.content_type}`,
                '_blank'
              )
            }
            style={{ cursor: 'pointer', color: '#1890ff' }}
          >
            {enTitle && <div style={{ fontWeight: 'bold' }}>{enTitle}</div>}
            {(koTitle || zhTitle) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {koTitle}
                {koTitle && zhTitle && ' / '}
                {zhTitle}
              </div>
            )}
            {!enTitle && !koTitle && !zhTitle && <div style={{ color: '#999' }}>Untitled</div>}
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '12%',
      sorter: true,
    },
    {
      title: 'SEASON / SERIES',
      dataIndex: 'season_data',
      key: 'season_series',
      width: '20%',
      render: (_text: string, record: ContentListInterface) => {
        const season = record.season;
        const series = record.series;

        if (!season && !series) {
          return <div style={{ color: '#999' }}>-</div>;
        }

        return (
          <div>
            {series?.id && (
              <div
                style={{
                  fontWeight: 'bold',
                  color: 'red',
                  cursor: 'pointer',
                }}
                onClick={e => {
                  e.stopPropagation();
                  window.open(`/series/edit2/${series.id}`, '_blank');
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Series: {series?.slug}
              </div>
            )}
            {season?.id && (
              <div
                style={{
                  color: 'blue',
                  marginTop: '4px',
                  cursor: 'pointer',
                }}
                onClick={e => {
                  e.stopPropagation();
                  window.open(`/season/edit2/${season.id}`, '_blank');
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.textDecoration = 'underline';
                  e.currentTarget.style.color = 'blue';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.textDecoration = 'none';
                  e.currentTarget.style.color = '#1890ff';
                }}
              >
                Season: {season?.slug}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Episode Type',
      dataIndex: 'content_type',
      key: 'content_type',
      width: '8%',
      render: (_text: string, record: ContentListInterface): string => record.content_type,
    },
    {
      title: 'EP #',
      dataIndex: 'episode_order',
      key: 'episode_order',
      width: '6%',
      sorter: true,
      render: (order: number | null) => {
        if (order == null) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return order;
      },
    },
    // {
    //   title: 'STATUS',
    //   dataIndex: 'status',
    //   key: 'status',
    //   width: '5%',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: '#52c41a', text: 'enabled' },
    //       unpublished: { color: '#999', text: 'disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: '#999', text: 'disabled' };
    //     return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
    //   },
    // },
    {
      title: 'INGEST SOURCE',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      width: '10%',
      render: (ingestSource: string | null | undefined) => {
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'id',
      key: 'actions',
      width: '10%',
      render: (_: any, record: ContentListInterface) => (
        <Space>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() =>
              window.open(
                `/episode/edit/${record.id || 0}?content_type=${record.content_type}`,
                '_blank'
              )
            }
          />
          <Button
            type="text"
            icon={<CheckCircleOutlined />}
            onClick={() =>
              setCheckTarget({ id: record.id, title: record.title_en || record.title || '' })
            }
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (!accountStore.hasPermission('content', 'delete')) {
                message.info('You do not have permission');
                return;
              }

              Modal.confirm({
                title: 'Really delete?',
                content: 'This action cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: async () => {
                  try {
                    await contentService.deleteContent(record.id);
                    message.success('Deleted successfully.');
                    window.location.href = '/library/';
                  } catch (error) {
                    console.error('Error deleting content:', error);
                  }
                },
              });
            }}
          />
        </Space>
      ),
    },
  ] as TableColumnInterface<ContentListInterface>[];

  const fetchContent = useCallback(
    async (params: URLSearchParams): Promise<{ data: ContentListInterface[]; count: number }> => {
      const contentList = await libraryService.fetch(params);
      const contents = contentList.results as unknown as ContentListInterface[];
      return { data: contents, count: contentList.count };
    },
    []
  );

  return (
    <Component.BasePageComponent>
      <div className="video-content-container">
        {/* Filter Section */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <Space wrap size="middle">
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Service Distribution
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="All Sources"
                value={serviceDistributionFilter}
                onChange={handleServiceDistributionChange}
                options={SERVICE_DISTRIBUTION_OPTIONS}
              />
            </div>
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Ingest Source
              </div>
              <Select
                mode="multiple"
                style={{ width: 200 }}
                placeholder="All Sources"
                value={ingestSourceFilter}
                onChange={handleIngestSourceFilterChange}
                options={INGEST_SOURCE_OPTIONS}
              />
            </div>
            {/* <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
              <Select
                style={{ width: 150 }}
                placeholder="All Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={STATUS_OPTIONS}
              />
            </div> */}
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Episode Type
              </div>
              <Select
                mode="multiple"
                style={{ width: 200 }}
                placeholder="All Types"
                value={contentTypeFilter}
                onChange={handleContentTypeChange}
                options={[...CONTENT_TYPE.filter(type => type !== 'movie'), 'shorts'].map(type => ({
                  value: type,
                  label: type,
                }))}
              />
            </div>
          </Space>
        </div>

        {/* Table */}
        <Component.TableComponent<ContentListInterface>
          name="Episode"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          dataFetch={fetchContent}
          columns={columns}
          actions={{
            onSelectionChange: setSelectedList,
            onAddClick: () => window.open(`/episode/edit/new?content_type=tv-series`, '_blank'),
          }}
          additionalButton={[
            <Link to="/episode/bulk-ingest" key="bulk-episode">
              <Button icon={<CloudUploadOutlined />} size="small">
                Bulk Episode Upload
              </Button>
            </Link>,
            ...(isPlatformOne
              ? [
                  <ContentBulkDistributionButton
                    key="bulk-distribution"
                    selectedList={selectedList}
                  />,
                ]
              : []),
          ]}
        />
      </div>
      <DistributionStatusModal
        open={!!checkTarget}
        onClose={() => setCheckTarget(null)}
        kind="episode"
        recordId={checkTarget?.id ?? null}
        recordTitle={checkTarget?.title}
      />
    </Component.BasePageComponent>
  );
};

export default observer(Library);
